import type { LimitOrderInputFormActionsFunctions } from 'state/secondaryMarket/limit-order/form/input/inputForm.actions';
import type { LimitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import type { LiveInputFormActionFunctions } from 'state/secondaryMarket/live/form/input/inputForm.actions';
import type { LiveSelectors } from 'state/secondaryMarket/live/live.selector';
import { liveThunks } from 'state/secondaryMarket/live/live.thunk';
import type { Settlement } from 'state/secondaryMarket/shared/referential/referential.model';
import type { ReferentialSelector } from 'state/secondaryMarket/shared/referential/referential.selector';
import { toSettlementTypeKey } from 'state/secondaryMarket/shared/utils';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import { getTabIsFrozen } from 'state/workspaces/tabs/tabs.selector';

type BookingInformationStateProps = {
  settlementDate: string | undefined;
  minSettlementDate?: string | undefined;
  settlementHolidays: ReadonlyArray<string> | undefined;
  availableSettlementTypes: ReadonlyArray<Settlement> | undefined;
  settlementType: string | undefined;
  isLoadingSettlement: boolean;
  isFrozen: boolean;
};
type BookingInformationOwnProps = {
  tabId: string;
  className?: string;
};
type BookingInformationDispatchProps = {
  onSettlementDateChanged: (value: Nullable<string>) => void;
  onSettlementTypeChanged: (value: Settlement | undefined) => void;
};

export type BookingInformationProps = BookingInformationStateProps &
  BookingInformationOwnProps &
  BookingInformationDispatchProps;
export const secondaryMarketBookingInformationConnectFactory = (
  referentialSelectors: ReferentialSelector,
  secondaryMarketSelectors: LiveSelectors | LimitOrderSelectors,
  inputFormActions: LiveInputFormActionFunctions | LimitOrderInputFormActionsFunctions,
) => {
  const mapStateToProps = (state: AppState, { tabId }: BookingInformationOwnProps): BookingInformationStateProps => {
    const availableSettlementTypes = referentialSelectors.getSettlements(state, tabId);

    const settlementDate = secondaryMarketSelectors.getFormSettlementDate(state, tabId);
    const minSettlementDate = referentialSelectors.getFirstSettlementDate(state, tabId);
    const settlementType = toSettlementTypeKey(secondaryMarketSelectors.getFormSettlement(state, tabId));
    const settlementHolidays = referentialSelectors.getSettlementHolidays(state, tabId);

    const clientLoadingStatus = referentialSelectors.getClientLoadingStatus(state, tabId) ?? 'idle';
    const settlementLoadingStatus = referentialSelectors.getSettlementLoadingStatus(state, tabId);

    const isFrozen = getTabIsFrozen(state, tabId) ?? false;

    return {
      settlementDate,
      minSettlementDate,
      availableSettlementTypes,
      settlementType,
      settlementHolidays,
      isLoadingSettlement: settlementLoadingStatus === 'loading' || clientLoadingStatus === 'loading',
      isFrozen,
    };
  };

  const mapDispatchToProps = (
    dispatch: ThunkDispatch,
    { tabId }: BookingInformationOwnProps,
  ): BookingInformationDispatchProps => {
    return {
      onSettlementDateChanged(value) {
        if (value !== null) {
          dispatch(inputFormActions.formFieldChanged(tabId, 'settlementDate', value));
        }
      },
      onSettlementTypeChanged: value => {
        dispatch(liveThunks.settlementTypeChangedThunk(tabId, value));
      },
    };
  };

  return { mapStateToProps, mapDispatchToProps };
};
